/**
 * Carteiras Modelo · Portfolio (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_modelo.py).
 *
 * Estratégia (carteira-modelo): composição atual, tearsheet e performance. A seção de
 * aderência das carteiras investidas (ComDinheiro) não foi migrada: decisão de
 * 2026-10-01, confirmada em 2026-10-03. Na URL: estratégia, período e os detalhes opcionais.
 */
import { Alert, Badge, Checkbox, Group, Loader, Select, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, CampoData, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { Tearsheet, type BlocoMovel, type TearsheetApi } from '../../components/Tearsheet';
import { formatarData } from '../../lib/formatos';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/carteiras-administradas/carteiras-modelo-portfolio';

interface Dados {
  opcoes: { estrategias: string[] };
  estrategia: string;
  composicao: { data: string; ativos: { codigo: string; peso: number }[] };
  periodo: { min: string; max: string; inicio: string; fim: string };
  tearsheet: TearsheetApi;
  performance: TabelaApi;
  historico?: TabelaApi;
  diario?: GraficoApi;
}
function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

export function CarteirasModeloPortfolio() {
  const [f, definir] = useFiltrosUrl({
    estrategia: '',
    inicio: '',
    fim: '',
    historico: false as boolean,
    diario: false as boolean,
  });
  const parametros = {
    estrategia: f.estrategia || undefined,
    inicio: f.inicio || undefined,
    fim: f.fim || undefined,
    historico: f.historico || undefined,
    diario: f.diario || undefined,
  };
  const consulta = useQuery({
    queryKey: ['carteiras.modelo', parametros],
    queryFn: ({ signal }) => apiGet<Envelope<Dados>>(CAMINHO, parametros, signal),
    placeholderData: keepPreviousData,
  });
  const d = consulta.data?.data;
  const buscarMovel = async (janela: string, sinal: AbortSignal) =>
    (await apiGet<Envelope<BlocoMovel>>(`${CAMINHO}/movel`, { ...parametros, janela }, sinal)).data;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Carteiras Modelo · Portfolio"
        descricao="Carteira-modelo de ações: composição atual, retorno diário com pesos de t−1 contra Ibovespa, SMLL e CDI."
        dadosAte={consulta.data?.meta.dados_ate}
        fonte="Fibery (carteiras-modelo) · Banco Persevera (preços, índices)"
      />
      <BarraFiltros carregando={consulta.isFetching}>
        <GrupoFiltros titulo="Estratégia">
          <Select
            label="Carteira-modelo"
            data={d?.opcoes.estrategias ?? []}
            value={f.estrategia || d?.estrategia || null}
            onChange={(estrategia) => estrategia && definir({ estrategia, inicio: '', fim: '' })}
            w={LARGURA.selecao - 60}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Período">
          <CampoData
            rotulo="Início"
            valor={f.inicio || d?.periodo.inicio || ''}
            min={d?.periodo.min}
            max={d?.periodo.max}
            aoMudar={(inicio) => definir({ inicio })}
          />
          <CampoData
            rotulo="Fim"
            valor={f.fim || d?.periodo.fim || ''}
            min={d?.periodo.min}
            max={d?.periodo.max}
            aoMudar={(fim) => definir({ fim })}
          />
        </GrupoFiltros>
      </BarraFiltros>
      <AvisosApi avisos={[consulta.data?.meta.avisos]} />
      {consulta.isError && (
        <Alert color="red">
          {mensagem(consulta.error, 'Não foi possível carregar a estratégia.')}
        </Alert>
      )}
      {consulta.isPending && <Loader />}
      {d && (
        <>
          <Secao
            titulo={`Composição atual · ${d.estrategia}`}
            legenda={`${d.composicao.ativos.length} ativos · ${formatarData(d.composicao.data)}`}
            acoes={
              <Checkbox
                label="Histórico de alocações"
                checked={f.historico}
                onChange={(e) => definir({ historico: e.currentTarget.checked })}
              />
            }
          >
            <Group gap={6}>
              {d.composicao.ativos.map((x) => (
                <Badge key={x.codigo} size="lg" color="#1e3a5f" radius="sm">
                  {x.codigo}{' '}
                  <Text span size="xs" opacity={0.75} fw={400}>
                    {x.peso.toLocaleString('pt-BR', {
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    })}
                    %
                  </Text>
                </Badge>
              ))}
            </Group>
            {f.historico && d.historico && <TabelaDados tabela={d.historico} altura={360} />}
          </Secao>
          <Tearsheet
            key={consulta.dataUpdatedAt}
            dados={d.tearsheet}
            chave={parametros}
            buscarMovel={buscarMovel}
          />
          <Secao
            titulo="Performance por janela"
            legenda="Retorno (%) em cada janela; cores por quartil na coluna."
            acoes={
              <Checkbox
                label="Retorno diário"
                checked={f.diario}
                onChange={(e) => definir({ diario: e.currentTarget.checked })}
              />
            }
          >
            <TabelaDados tabela={d.performance} alturaAutomatica />
            {f.diario && d.diario && <Grafico grafico={d.diario} />}
          </Secao>
        </>
      )}
    </Pagina>
  );
}
